import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import type { ElementCategoryRow } from './data/api';
import { CATEGORY_ICON, DEFAULT_CATEGORY_ICON } from './data/categoryVisuals';

export function ElementCategoryCardSkeleton(): ReactNode {
  return (
    <Box sx={{ minWidth: 200, maxWidth: 250, p: 2 }}>
      <Skeleton variant="rectangular" height={116} sx={{ borderRadius: 1 }} />
    </Box>
  );
}

export function ElementCategoryCard({
  category,
  selected,
  onSelect,
}: {
  category: ElementCategoryRow;
  selected: boolean;
  onSelect: () => void;
}): ReactNode {
  const theme = useTheme();
  const Icon = CATEGORY_ICON[category.category] ?? DEFAULT_CATEGORY_ICON;
  const accent =
    theme.palette.elementCategory[category.category] ?? theme.palette.elementCategory.default;
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      sx={{
        textAlign: 'center',
        cursor: 'pointer',
        p: 2,
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        minWidth: 200,
        maxWidth: 250,
        border: '1.5px solid',
        borderColor: selected ? accent : 'transparent',
        backgroundColor: alpha(accent, selected ? 0.16 : 0.08),
        transition: 'box-shadow 150ms ease, border-color 150ms ease, transform 150ms ease',
        '&:hover': {
          borderColor: accent,
          boxShadow: (theme) =>
            `0 4px 12px ${alpha(accent, theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: alpha('#fff', 0.7),
          borderRadius: 2,
          display: 'flex',
          height: 28,
          justifyContent: 'center',
          width: 28,
        }}
      >
        <Icon fontSize="small" sx={{ color: accent }} />
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{category.category}</Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
        {category.elementsCount} Elements
      </Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
        {category.tagsCount} Tags
      </Typography>
    </Box>
  );
}
